import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/auth";
import { useEventTracking } from "../hooks/use-posthog";

/**
 * PostHog Auth Integration Component
 *
 * This component handles PostHog user identification based on authentication state.
 * According to PostHog best practices:
 * - Call identify() when user logs in or app loads with authenticated user
 * - Call reset() when user logs out
 * - Only identify once per session unless user changes
 */
export const PostHogAuthIntegration = () => {
  const user = useAuthStore((state) => state.user);
  const { identifyUser, resetUser, getCurrentDistinctId } = useEventTracking();
  const lastIdentifiedUserId = useRef<string | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Skip if PostHog is not available
    if (!identifyUser || !resetUser) {
      return;
    }

    const currentDistinctId = getCurrentDistinctId();
    console.log(
      "PostHog Auth Integration - Current distinct ID:",
      currentDistinctId,
      "User:",
      user?.id,
    );

    // Case 1: User is logged in
    if (user && !user.isAnonymous) {
      // Only identify if:
      // - We haven't identified this user yet, OR
      // - The user has changed (different user ID)
      if (lastIdentifiedUserId.current !== user.id) {
        console.log("PostHog Auth Integration - Identifying user:", user.id);
        identifyUser(user);
        lastIdentifiedUserId.current = user.id;
      } else {
        console.log("PostHog Auth Integration - User already identified, skipping");
      }
    }

    // Case 2: User logged out (was logged in, now null)
    else if (!user && lastIdentifiedUserId.current && hasInitialized.current) {
      console.log("PostHog Auth Integration - User logged out, resetting PostHog session");
      resetUser();
      lastIdentifiedUserId.current = null;
    }

    // Case 3: Anonymous user - do nothing (let PostHog handle anonymous tracking)
    else if (user?.isAnonymous) {
      console.log("PostHog Auth Integration - Anonymous user, no identification needed");
    }

    hasInitialized.current = true;
  }, [user, identifyUser, resetUser, getCurrentDistinctId]);

  // This component doesn't render anything
  return null;
};
